export default abstract class Component<T> {

    private static className = 'component';
    private static attrName = 'data-com-id';
    private static id: number = 1;
    private id: string = this.className + Component.id++;
    private _viewModel: T;
    private _element: HTMLElement;

    //--------------------------------------------------------------------------------------
    // Subclasses must provide the template and the model for the component
    //--------------------------------------------------------------------------------------
    constructor(protected template: (c: T, config: any) => string, viewModel: T) {
        this._viewModel = viewModel;
    }

    //--------------------------------------------------------------------------------------
    // Subclasses may manipulate (but not replace) the viewmodel
    //--------------------------------------------------------------------------------------
    protected get viewModel() {
        return this._viewModel;
    }

    //--------------------------------------------------------------------------------------
    // Subclasses may manipulate (but not replace) the element
    //--------------------------------------------------------------------------------------
    protected get element() {
        return this._element;
    }

    //--------------------------------------------------------------------------------------
    // Subclasses must implement the bind method
    // If desired, event binding should take place in this method.
    // If the viewModel contains components not in the top level of the viewModel, 
    // the bind method should call applyBindings on those componets
    //--------------------------------------------------------------------------------------
    protected abstract bind(element: HTMLElement, model: T): void;

    //--------------------------------------------------------------------------------------
    // Calling this method wil invoke the bind method of the class and apply bindings to all 
    // components in the top level of the viewModel
    //--------------------------------------------------------------------------------------
    applyBindings(ancestor: HTMLElement) {
        this._element = this.getElement(ancestor);
                
        this.onApplyBindings();
    }

    //--------------------------------------------------------------------------------------
    // Calling this method will refresh the view to reflect the contents of the viewModel
    //--------------------------------------------------------------------------------------
    protected refresh() {
        this.onRefresh();
    }

    //--------------------------------------------------------------------------------------
    // This method may be overridden by classes extending the component class
    //--------------------------------------------------------------------------------------
    protected onApplyBindings() {
        this.bind(this.element || document.createElement('div'), this.viewModel);

        for (let p in this.viewModel) {
            if (this.viewModel.hasOwnProperty(p)) {
                let c = this.prop(this.viewModel, p);
                if (c instanceof Component) {
                    c.applyBindings(this.element);
                }
            }
        }
    }

    //--------------------------------------------------------------------------------------
    // This method may be overridden by classes extending the component class
    //--------------------------------------------------------------------------------------
    protected onRender() : HTMLElement {
        let parent = document.createElement('div');
        parent.innerHTML = this.template(
            this.viewModel,
            {
                allowedProtoMethods: {
                    html: true
                },
                allowedProtoProperties: {
                    html: true
                }
            });
        let elements = parent.children;

        if (elements.length !== 1)
            throw new Error('The template for ' + this.className + ' should contain exactly one element');
      
		let element = elements[0] as HTMLElement
	  
        let existingId = elements[0].getAttribute(Component.attrName);
        if (existingId)
            throw new Error('A component ('+existingId.replace(/\d+/,'')+') should not be on the top level of another component ('+this.className+')');

        element.setAttribute(Component.attrName, this.id);
        element.classList.add(Component.className, Component.className + '-' + this.className);

        return element;
    }
   
    //--------------------------------------------------------------------------------------
    // This method may be overridden by classes extending the component class
    //--------------------------------------------------------------------------------------
    protected onRefresh() {
        let parent = this.element.parentElement;
        if (parent) {
            this.element.outerHTML = this.render().outerHTML;
            this.applyBindings(parent || document.createElement('div'));
        }
    }

    private get className() {
        return this.constructor.name || ((/function (.{1,})\(/).exec(this.constructor.toString()) || ['', ''])[1];
    }

    private getElement(ancestor: Element): HTMLElement {
        let selector = '[' + Component.attrName + '=\'' + this.id + '\']';
        return (ancestor.querySelector(selector) as HTMLElement) || document.createElement('div');
    }

    private prop<T, K extends keyof T>(obj: T, key: K) {
        return obj[key];
    }

    get html() {
        return this.render().outerHTML;
    }

    private render(): HTMLElement {
        return this.onRender();
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./Frontend/src/component/component.ts